import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  FiEdit2,
  FiMoreVertical,
  FiPlus,
  FiRefreshCw,
  FiTrash2,
} from "react-icons/fi";
import { useNewton } from "../../store/NewtonContext";
import { useUserData } from "../../../../../../hooks/useUserData";
import { useUpdateSessionDisplayName } from "../../hooks/useUpdateSessionDisplayName";
import { useLlmSessionCreate } from "../../hooks/useLlmSessionCreate";
import { useLlmSessionDelete } from "../../hooks/useLlmSessionDelete";
import { useLlmSessions } from "../../hooks/useLlmSessions";
import { useLlmSessionById } from "../../hooks/useLlmSessionById";
import Button from "../../../../../../components/shared/Button/Button";
import Popover from "../../../../../../components/shared/Popover/Popover";
import { twMerge } from "tailwind-merge";

const formatDisplayName = (session) => {
  const sessionId = session?.sessionId;
  return (
    session?.displayName ||
    (sessionId ? `Chat ${sessionId.slice(-6)}` : "Untitled chat")
  );
};

export function SessionMenu() {
  const {
    sessionId: activeSessionId,
    setMessagesHistory,
    setSessionId,
    pendingSessionId,
    setPendingSessionId,
    adminOverrideUserId,
  } = useNewton();
  const { userData } = useUserData();
  const effectiveUserId = useMemo(
    () => adminOverrideUserId || userData?.uid,
    [adminOverrideUserId, userData?.uid],
  );
  const createSession = useLlmSessionCreate({
    userId: userData?.uid,
    onSuccess: () => {
      setMessagesHistory([]);
    },
  });

  const [editingId, setEditingId] = useState(null);
  const [draftName, setDraftName] = useState("");
  const [menuOpenId, setMenuOpenId] = useState(null);

  const { llmSessions, isLoading, isError } = useLlmSessions({
    userId: effectiveUserId,
  });

  const sessions = llmSessions?.sessions || [];

  const { mutateAsync: loadSessionMessages } = useLlmSessionById();

  // Function to change session and load its messages
  const changeSession = useCallback(
    async (sessionId) => {
      setSessionId(sessionId);
      try {
        const result = await loadSessionMessages({ sessionId });
        const messages = Array.isArray(result?.messages) ? result.messages : [];
        setMessagesHistory(messages);
      } catch (error) {
        console.error("Failed to load session messages:", error);
        setMessagesHistory([]);
      }
    },
    [loadSessionMessages, setMessagesHistory, setSessionId],
  );

  // Decide which session to activate on open:
  // 1. A pending session from "Dive Deeper" takes priority.
  // 2. Otherwise fall back to the first session in the list once it loads.
  useEffect(() => {
    if (pendingSessionId) {
      changeSession(pendingSessionId);
      setPendingSessionId(null);
      return;
    }

    if (!activeSessionId && sessions.length > 0) {
      changeSession(sessions[0].sessionId);
    }
  }, [pendingSessionId, activeSessionId, sessions, changeSession, setPendingSessionId]);

  const updateDisplayName = useUpdateSessionDisplayName({
    userId: userData?.uid,
    onSuccess: () => setEditingId(null),
  });

  const deleteSession = useLlmSessionDelete({
    userId: userData?.uid,
    onSuccess: (_data, variables) => {
      const deletedId = variables?.sessionId;
      if (deletedId === activeSessionId) {
        setMessagesHistory([]);
        setSessionId(null);
      }

      setMenuOpenId(null);
    },
  });

  const handleSelect = (session) => {
    const selectedId = session?.sessionId;
    if (!selectedId) return;

    changeSession(selectedId);
    setMenuOpenId(null);
  };

  const handleEdit = (session) => {
    const selectedId = session?.sessionId;
    if (!selectedId) return;

    setEditingId(selectedId);
    setDraftName(session?.displayName || "");
    setMenuOpenId(null);
  };

  const handleSave = (targetSessionId) => {
    const nextName = (draftName || "").trim();
    if (!nextName) {
      setEditingId(null);
      return;
    }

    updateDisplayName.mutate({
      displayName: nextName,
      sessionId: targetSessionId,
    });
  };

  const handleToggleMenu = (targetSessionId) => {
    setMenuOpenId((prev) =>
      prev === targetSessionId ? null : targetSessionId,
    );
  };

  const handleRename = (session) => {
    handleEdit(session);
    setMenuOpenId(null);
  };

  const handleDelete = (sessionId) => {
    deleteSession.mutate({ sessionId });
  };

  return (
    <div
      className={twMerge(
        "w-[168px] shrink-0 flex flex-col overflow-hidden",
        "border-y-0 border-l-0 border-r border-grayTrue800",
        "min-h-0",
      )}
    >
      <div className="pb-3 pt-4 px-2 scrollbar-hide flex-1 min-h-0 overflow-y-auto">
        {isLoading ? (
          <div className="text-grayTrue400 text-xs">Loading sessions…</div>
        ) : isError ? (
          <div className="text-error500 text-xs">
            Failed to load sessions. Try again.
          </div>
        ) : sessions.length === 0 ? (
          <div className="text-grayTrue400 text-xs">
            No previous conversations yet.
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            {sessions.map((session) => {
              const sessionId = session?.sessionId;
              const isActive = sessionId === activeSessionId;
              const isEditing = editingId === sessionId;
              const label = formatDisplayName(session);
              const isDeleting =
                deleteSession.isPending &&
                deleteSession.variables?.sessionId === sessionId;

              return (
                <div key={`${session.name || sessionId}`}>
                  <div
                    className={twMerge(
                      "relative group",
                      "w-full flex items-center justify-between rounded-md",
                      "h-10 min-h-10",
                      isActive ? "bg-grayTrue700" : "hover:bg-grayTrue700",
                    )}
                  >
                    {/* Select chat */}

                    {isEditing ? (
                      <input
                        autoFocus
                        className="mx-2 bg-grayTrue900 text-gray50 w-full rounded-sm px-2 py-1 text-xs outline-none"
                        onChange={(e) => setDraftName(e.target.value)}
                        onClick={(e) => e.stopPropagation()}
                        onKeyDown={(e) => {
                          if (e.key === "Enter") {
                            e.preventDefault();
                            handleSave(sessionId);
                          }
                          if (e.key === "Escape") setEditingId(null);
                        }}
                        value={draftName}
                      />
                    ) : (
                      <>
                        <button
                          type="button"
                          className="px-3 min-w-0 h-full flex-1 text-left"
                          aria-selected={isActive}
                          onClick={() => handleSelect(session)}
                        >
                          <div className="text-gray100 text-sm font-semibold truncate">
                            {label}
                          </div>
                        </button>
                        <Popover
                          align="end"
                          side="bottom"
                          offset={6}
                          containerClassName="h-full"
                          contentClassName="w-36 overflow-hidden p-1 flex flex-col gap-1 rounded-md border border-grayTrue700 bg-grayTrue800 shadow-xl"
                          isOpen={menuOpenId === sessionId}
                          onClose={() => setMenuOpenId(null)}
                          trigger={
                            <button
                              type="button"
                              aria-label="Open session actions"
                              className="text-grayTrue400 opacity-0 transition-opacity group-hover:opacity-100 px-2 h-full"
                              onClick={(e) => {
                                e.preventDefault();
                                e.stopPropagation();
                                handleToggleMenu(sessionId);
                              }}
                            >
                              <FiMoreVertical className="size-4" />
                            </button>
                          }
                        >
                          <Button
                            className="w-full flex justify-start"
                            rounded="md"
                            isDisabled={adminOverrideUserId}
                            color="transparent/grayTrue700"
                            onClick={() => handleRename(session)}
                            size="xs"
                            startContent={<FiEdit2 className="size-4" />}
                            type="button"
                          >
                            Rename
                          </Button>
                          <Button
                            className="w-full flex justify-start text-error500"
                            rounded="md"
                            color="transparent/grayTrue700"
                            isDisabled={isDeleting || adminOverrideUserId}
                            onClick={() => handleDelete(sessionId)}
                            size="xs"
                            startContent={<FiTrash2 className="size-4" />}
                            type="button"
                          >
                            {isDeleting ? "Deleting…" : "Delete"}
                          </Button>
                        </Popover>
                      </>
                    )}
                  </div>

                  {isEditing && (
                    <div className="mt-1 flex items-center gap-2">
                      <Button
                        color="primary500"
                        className="text-[10px] px-2"
                        rounded="md"
                        size="xxs"
                        onClick={() => handleSave(sessionId)}
                        type="button"
                      >
                        {updateDisplayName.isPending ? "Saving…" : "Save"}
                      </Button>
                      <Button
                        color="transparent/grayTrue700"
                        className="text-[10px] px-2"
                        rounded="md"
                        size="xxs"
                        onClick={() => setEditingId(null)}
                        type="button"
                      >
                        Cancel
                      </Button>
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )}
      </div>

      <div className="w-full px-2 pb-6">
        <Button
          className="w-full"
          color="white/gray300"
          disabled={
            adminOverrideUserId || createSession.isPending || !userData?.uid
          }
          onClick={() => createSession.mutate()}
          size="sm"
        >
          {createSession.isPending ? (
            <FiRefreshCw className="animate-spin size-5" />
          ) : (
            <FiPlus className="size-5" />
          )}
          New chat
        </Button>
      </div>
    </div>
  );
}
